import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { socialService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import Lottie from './Lottie'
import './FriendsSidebar.css'

function RequestItem({ request, onAccept, onReject, isAccepting, isRejecting, showSuccess }) {
  const acceptLottieRef = useRef(null)
  const denyLottieRef = useRef(null)
  const successLottieRef = useRef(null)
  const [acceptAnimation, setAcceptAnimation] = useState(null)
  const [denyAnimation, setDenyAnimation] = useState(null)
  const [successAnimation, setSuccessAnimation] = useState(null)

  // Charger les animations
  useEffect(() => {
    fetch('/Accept.json')
      .then(res => res.json())
      .then(data => setAcceptAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Accept.json:', err))
    
    fetch('/Denyv2.json')
      .then(res => res.json())
      .then(data => setDenyAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Denyv2.json:', err))
    
    fetch('/Succes.json')
      .then(res => res.json())
      .then(data => setSuccessAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Succes.json:', err))
  }, [])

  // Jouer l'animation de succès quand elle apparaît
  useEffect(() => {
    if (showSuccess && successLottieRef.current && successAnimation) {
      successLottieRef.current.goToAndPlay(0)
    }
  }, [showSuccess, successAnimation])

  // Si on est en train d'accepter ou de montrer le succès, afficher uniquement l'animation
  if (isAccepting || showSuccess) {
    return (
      <div className="request-item request-item-loading">
        {showSuccess ? (
          <div className="success-indicator-full">
            {successAnimation && (
              <Lottie
                lottieRef={successLottieRef}
                animationData={successAnimation}
                loop={false}
                autoplay={false}
                style={{ width: 60, height: 60 }}
              />
            )}
            <span className="success-text">Accepté !</span>
          </div>
        ) : (
          <div className="loading-indicator-full">
            <div className="loader"></div>
            <span className="loading-text">Acceptation...</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="request-item">
      <div className="request-user">
        <div 
          className="user-avatar"
          style={{
            backgroundImage: request.sender?.avatar_url ? `url(${request.sender.avatar_url})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {!request.sender?.avatar_url && (
            (request.sender?.display_name?.charAt(0) || 
             request.sender?.username?.charAt(0) || 
             request.sender?.email?.charAt(0) || 
             'U').toUpperCase()
          )}
        </div>
        <span>
          {request.sender?.display_name || 
           (request.sender?.username ? `@${request.sender.username}` : null) || 
           request.sender?.email || 
           'Utilisateur'}
        </span>
      </div>
      <div className="request-actions">
        {acceptAnimation && (
          <div
            className="accept-btn-lottie"
            onClick={() => {
              if (acceptLottieRef.current) {
                acceptLottieRef.current.goToAndPlay(0)
              }
              onAccept(request.id)
            }}
          >
            <Lottie
              lottieRef={acceptLottieRef}
              animationData={acceptAnimation}
              loop={false}
              autoplay={false}
              style={{ width: 32, height: 32 }}
            />
          </div>
        )}
        {denyAnimation && !isRejecting && (
          <div
            className="reject-btn-lottie"
            onClick={() => {
              if (denyLottieRef.current) {
                denyLottieRef.current.goToAndPlay(0)
              }
              onReject(request.id)
            }}
          >
            <Lottie
              lottieRef={denyLottieRef}
              animationData={denyAnimation}
              loop={false}
              autoplay={false}
              style={{ width: 32, height: 32 }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function FriendsSidebar({ fullWidth = false, onUserClick }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [showRequests, setShowRequests] = useState(false)

  // Récupérer les amis
  const { data: friendsData, error: friendsError } = useQuery({
    queryKey: ['social-friends'],
    queryFn: () => socialService.getFriends(),
    retry: false,
    enabled: true
  })

  // Récupérer les demandes d'amis
  const { data: requestsData, error: requestsError } = useQuery({
    queryKey: ['social-friend-requests'],
    queryFn: () => socialService.getFriendRequests(),
    retry: false,
    enabled: true
  })

  // Recherche d'utilisateurs
  const { data: searchResults, error: searchError } = useQuery({
    queryKey: ['social-search-users', searchQuery],
    queryFn: () => socialService.searchUsers(searchQuery),
    enabled: searchQuery.length > 2,
    retry: false
  })

  const friends = friendsData?.friends || []
  const requests = requestsData?.requests || []
  const users = searchResults?.users || []

  // Mutation pour envoyer une demande d'ami
  const sendRequestMutation = useMutation({
    mutationFn: (userId) => socialService.sendFriendRequest(userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['social-friend-requests'])
      queryClient.invalidateQueries(['social-search-users'])
      // Afficher l'animation de succès
      setShowSuccessMessage(true)
      setTimeout(() => {
        if (successLottieRef.current && successAnimation) {
          successLottieRef.current.goToAndPlay(0)
        }
      }, 100)
      // Masquer après 3 secondes
      setTimeout(() => {
        setShowSuccessMessage(false)
      }, 3000)
    },
    onError: (error) => {
      // Vérifier si c'est une erreur de demande déjà envoyée
      const errorMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message || ''
      const errorMessageLower = errorMessage.toLowerCase()
      console.log('[FriendsSidebar] Erreur détectée:', errorMessage, 'Lower:', errorMessageLower)
      
      // Vérifier plusieurs variantes du message d'erreur
      if (errorMessageLower.includes('existe déjà') ||
          errorMessageLower.includes('déjà') ||
          errorMessageLower.includes('already') ||
          errorMessageLower.includes('une demande d\'ami existe déjà') ||
          errorMessageLower.includes('demande d\'ami existe')) {
        console.log('[FriendsSidebar] Détection: demande déjà envoyée - Affichage du modal pending, refuseAnimation:', !!refuseAnimation)
        // Afficher le message avec l'animation refuse
        // L'animation sera jouée automatiquement par le useEffect quand la ref sera disponible
        setShowPendingMessage(true)
        // Masquer après 3 secondes
        setTimeout(() => {
          setShowPendingMessage(false)
        }, 3000)
      } else {
        // Autre erreur, afficher un alert
        console.log('[FriendsSidebar] Autre erreur, affichage alert:', errorMessage)
        alert(errorMessage || 'Erreur lors de l\'envoi de la demande d\'ami')
      }
    }
  })

  // État pour suivre les demandes en cours de traitement
  const [processingRequests, setProcessingRequests] = useState(new Set())
  const [successRequests, setSuccessRequests] = useState(new Set())

  // États pour les animations de succès/pending
  const [successAnimation, setSuccessAnimation] = useState(null)
  const successLottieRef = useRef(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [refuseAnimation, setRefuseAnimation] = useState(null)
  const refuseLottieRef = useRef(null)
  const [showPendingMessage, setShowPendingMessage] = useState(false)

  useEffect(() => {
    fetch('/Succes.json')
      .then(res => res.json())
      .then(data => setSuccessAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Succes.json:', err))
    
    fetch('/refuse.json')
      .then(res => res.json())
      .then(data => setRefuseAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de refuse.json:', err))
  }, [])



  // Mutation pour accepter une demande
  const acceptRequestMutation = useMutation({
    mutationFn: async ({ requestId, senderId }) => {
      setProcessingRequests(prev => new Set(prev).add(requestId))
      try {
        const result = await socialService.acceptFriendRequest(requestId)
        // Attendre un peu pour vérifier que c'est bien stocké
        await new Promise(resolve => setTimeout(resolve, 500))
        return { result, senderId }
      } finally {
        setProcessingRequests(prev => {
          const newSet = new Set(prev)
          newSet.delete(requestId)
          return newSet
        })
      }
    },
    onSuccess: (data, variables) => {
      const { requestId, senderId } = variables
      setSuccessRequests(prev => new Set(prev).add(requestId))
      queryClient.invalidateQueries(['social-friends'])
      queryClient.invalidateQueries(['social-friend-requests'])
      // Invalider les profils des deux utilisateurs pour mettre à jour le compteur d'amis
      if (senderId) {
        queryClient.invalidateQueries(['social-user-profile', senderId])
      }
      // Invalider aussi le profil de l'utilisateur connecté
      if (user?.id) {
        queryClient.invalidateQueries(['social-user-profile', user.id])
      }
      // Retirer le succès après 3 secondes
      setTimeout(() => {
        setSuccessRequests(prev => {
          const newSet = new Set(prev)
          newSet.delete(requestId)
          return newSet
        })
      }, 3000)
    },
    onError: (error, variables) => {
      const { requestId } = variables || {}
      if (requestId) {
        setProcessingRequests(prev => {
          const newSet = new Set(prev)
          newSet.delete(requestId)
          return newSet
        })
      }
      // Afficher un message d'erreur approprié
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erreur lors de l\'acceptation de la demande'
      console.error('[FriendsSidebar] Erreur lors de l\'acceptation:', errorMessage)
      alert(errorMessage)
    }
  })

  // Mutation pour rejeter une demande
  const rejectRequestMutation = useMutation({
    mutationFn: (requestId) => socialService.rejectFriendRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries(['social-friend-requests'])
    }
  })

  // Mutation pour supprimer un ami
  const removeFriendMutation = useMutation({
    mutationFn: (friendId) => socialService.removeFriend(friendId),
    onSuccess: (data, friendId) => {
      queryClient.invalidateQueries(['social-friends'])
      // Invalider les profils des deux utilisateurs pour mettre à jour le compteur d'amis
      if (friendId) {
        queryClient.invalidateQueries(['social-user-profile', friendId])
      }
      // Invalider aussi le profil de l'utilisateur connecté
      if (user?.id) {
        queryClient.invalidateQueries(['social-user-profile', user.id])
      }
    }
  })

  const [openMenus, setOpenMenus] = useState(new Set())
  const [removeAnimation, setRemoveAnimation] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState(null) // { friendId, friend }

  useEffect(() => {
    fetch('/Remove.json')
      .then(res => res.json())
      .then(data => setRemoveAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Remove.json:', err))
  }, [])

  const toggleMenu = (friendId, e) => {
    e.stopPropagation()
    setOpenMenus(prev => {
      const newSet = new Set(prev)
      if (newSet.has(friendId)) {
        newSet.delete(friendId)
      } else {
        // Fermer tous les autres menus
        newSet.clear()
        newSet.add(friendId)
      }
      return newSet
    })
  }

  // Fermer les menus quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.friend-menu-container')) {
        setOpenMenus(new Set())
      }
    }

    if (openMenus.size > 0) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [openMenus])

  const handleRemoveFriend = (friendId, friend, e) => {
    e.stopPropagation()
    setConfirmDialog({ friendId, friend })
    setOpenMenus(prev => {
      const newSet = new Set(prev)
      newSet.delete(friendId)
      return newSet
    })
  }

  const confirmRemoveFriend = () => {
    if (confirmDialog) {
      removeFriendMutation.mutate(confirmDialog.friendId)
      setConfirmDialog(null)
    }
  }

  const cancelRemoveFriend = () => {
    setConfirmDialog(null)
  }

  const handleSendRequest = (userId) => {
    sendRequestMutation.mutate(userId)
  }

  const handleAccept = (requestId) => {
    const request = requests.find(r => r.id === requestId)
    const senderId = request?.sender?.id
    acceptRequestMutation.mutate({ requestId, senderId })
  }

  const handleReject = (requestId) => {
    rejectRequestMutation.mutate(requestId)
  }

  return (
    <div className={`friends-sidebar ${fullWidth ? 'full-width' : ''}`}>
      <div className="friends-header">
        <h3>
          <img src="/Friends_icon.png" alt="Amis" className="friends-icon" />
          Amis
        </h3>
        <button
          className="requests-btn"
          onClick={() => setShowRequests(!showRequests)}
        >
          {requests.length > 0 && <span className="badge">{requests.length}</span>}
          Demandes
        </button>
      </div>

      <div className={`requests-section ${showRequests ? 'open' : ''}`}>
        <h4>Demandes d'amis</h4>
        {requestsError?.response?.status === 405 ? (
          <p className="no-friends" style={{ color: '#ff6b6b' }}>
            ⚠️ Endpoint non disponible
          </p>
        ) : requests.length === 0 ? (
          <p className="no-friends">Aucune demande</p>
        ) : (
          requests.map(request => (
            <RequestItem
              key={request.id}
              request={request}
              onAccept={handleAccept}
              onReject={handleReject}
              isAccepting={processingRequests.has(request.id)}
              isRejecting={false}
              showSuccess={successRequests.has(request.id)}
            />
          ))
        )}
      </div>

      <div className="search-section">
        <div className="search-container-animated">
          <input
            type="text"
            className="search-input-animated"
            placeholder="Rechercher des utilisateurs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="search-icon-animated">
            <svg xmlns="http://www.w3.org/2000/svg" className="ionicon" viewBox="0 0 512 512">
              <title>Search</title>
              <path d="M221.09 64a157.09 157.09 0 10157.09 157.09A157.1 157.1 0 00221.09 64z" fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="32"></path>
              <path fill="none" stroke="currentColor" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="32" d="M338.29 338.29L448 448"></path>
            </svg>
          </div>
        </div>
      </div>

      {searchQuery.length > 2 && (
        <div className="search-results">
          <h4>Résultats de recherche</h4>
          {users.length === 0 ? (
            <p className="no-results">Aucun utilisateur trouvé</p>
          ) : (
            users.map(userItem => (
              <div key={userItem.id} className="user-item">
                <div
                  className="user-info"
                  onClick={() => onUserClick && onUserClick(userItem.id)}
                >
                  <div 
                    className="user-avatar"
                    style={{
                      backgroundImage: userItem.avatar_url ? `url(${userItem.avatar_url})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {!userItem.avatar_url && (
                      (userItem.display_name?.charAt(0) || 
                       userItem.username?.charAt(0) || 
                       userItem.email?.charAt(0) || 
                       'U').toUpperCase()
                    )}
                  </div>
                  <span>
                    {userItem.display_name || 
                     (userItem.username ? `@${userItem.username}` : null) || 
                     userItem.email || 
                     'Utilisateur'}
                  </span>
                </div>
                {userItem.id !== user?.id && (
                  <div
                    className="plusButton add-friend-btn"
                    onClick={() => !sendRequestMutation.isLoading && handleSendRequest(userItem.id)}
                    style={{ cursor: sendRequestMutation.isLoading ? 'not-allowed' : 'pointer', opacity: sendRequestMutation.isLoading ? 0.5 : 1 }}
                    tabIndex={0}
                  >
                    <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                      <g mask="url(#mask0_21_345)">
                        <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                      </g>
                    </svg>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      <div className="friends-list">
        <h4>Mes amis ({friends.length})</h4>
        {friendsError?.response?.status === 405 ? (
          <p className="no-friends" style={{ color: '#ff6b6b' }}>
            ⚠️ Endpoint non disponible
          </p>
        ) : friends.length === 0 ? (
          <p className="no-friends">Aucun ami pour le moment</p>
        ) : (
          friends.map(friend => (
            <div
              key={friend.id}
              className="friend-item"
            >
              <div 
                className="friend-item-content"
                onClick={() => onUserClick && onUserClick(friend.id)}
              >
                <div 
                  className="user-avatar"
                  style={{
                    backgroundImage: friend.avatar_url ? `url(${friend.avatar_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {!friend.avatar_url && (
                    (friend.display_name?.charAt(0) || 
                     friend.username?.charAt(0) || 
                     friend.email?.charAt(0) || 
                     'U').toUpperCase()
                  )}
                </div>
                <span>
                  {friend.display_name || 
                   (friend.username ? `@${friend.username}` : null) || 
                   friend.email || 
                   'Utilisateur'}
                </span>
              </div>
              <div className="friend-menu-container" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className={`hamburger-button ${openMenus.has(friend.id) ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleMenu(friend.id, e)
                  }}
                >
                  <div className="line1"></div>
                  <div className="line2"></div>
                  <div className="line3"></div>
                </button>
                {openMenus.has(friend.id) && (
                  <div className="liquid-glass-menu" onClick={(e) => e.stopPropagation()}>
                    <div 
                      className="menu-item remove-item"
                      onClick={(e) => handleRemoveFriend(friend.id, friend, e)}
                    >
                      {removeAnimation && (
                        <Lottie
                          animationData={removeAnimation}
                          loop={false}
                          autoplay={false}
                          style={{ width: 20, height: 20 }}
                        />
                      )}
                      <span>Supprimer</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      {confirmDialog && (
        <RemoveFriendConfirmDialog
          friend={confirmDialog.friend}
          onConfirm={confirmRemoveFriend}
          onCancel={cancelRemoveFriend}
        />
      )}

      {/* Animation de succès pour demande d'ami - Rendu directement comme dans UserProfile */}
      {showSuccessMessage && (
        <div className="success-message-overlay" onClick={() => setShowSuccessMessage(false)}>
          <div className="success-message-modal" onClick={(e) => e.stopPropagation()}>
            {successAnimation && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Lottie
                  lottieRef={successLottieRef}
                  animationData={successAnimation}
                  loop={false}
                  autoplay={true}
                  style={{ width: 120, height: 120, margin: '0 auto' }}
                />
              </div>
            )}
            <h3>Demande d'ami envoyée avec succès</h3>
          </div>
        </div>
      )}

      {/* Message pour demande déjà envoyée - Rendu directement comme dans UserProfile */}
      {showPendingMessage && (
        <div className="success-message-overlay" onClick={() => setShowPendingMessage(false)}>
          <div className="success-message-modal" onClick={(e) => e.stopPropagation()}>
            {refuseAnimation && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Lottie
                  lottieRef={refuseLottieRef}
                  animationData={refuseAnimation}
                  loop={false}
                  autoplay={true}
                  style={{ width: 120, height: 120, margin: '0 auto' }}
                />
              </div>
            )}
            <h3>Vous avez déjà envoyé une demande d'ami</h3>
            <p style={{ marginTop: '10px', color: 'var(--text-secondary)', fontSize: '0.9em', textAlign: 'center' }}>
              La demande est en attente de réponse
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// Composant de confirmation de suppression
function RemoveFriendConfirmDialog({ friend, onConfirm, onCancel }) {
  const acceptLottieRef = useRef(null)
  const denyLottieRef = useRef(null)
  const [acceptAnimation, setAcceptAnimation] = useState(null)
  const [denyAnimation, setDenyAnimation] = useState(null)

  useEffect(() => {
    // Charger Accept.json
    fetch('/Accept.json')
      .then(res => res.json())
      .then(data => setAcceptAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Accept.json:', err))
    
    // Charger Denyv2.json - exactement comme Accept.json
    fetch('/Denyv2.json')
      .then(res => res.json())
      .then(data => setDenyAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Denyv2.json:', err))
  }, [])

  const handleConfirm = () => {
    if (acceptLottieRef.current) {
      acceptLottieRef.current.goToAndPlay(0)
    }
    setTimeout(() => onConfirm(), 300)
  }

  const handleCancel = () => {
    if (denyLottieRef.current) {
      denyLottieRef.current.goToAndPlay(0)
    }
    setTimeout(() => onCancel(), 300)
  }

  return (
    <div className="confirm-dialog-overlay" onClick={onCancel}>
      <div className="confirm-dialog-motion" onClick={(e) => e.stopPropagation()}>
      <div className="confirm-dialog glass-card">
        <div className="confirm-dialog-header">
          <div 
            className="confirm-user-avatar"
            style={{
              backgroundImage: friend?.avatar_url ? `url(${friend.avatar_url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {!friend?.avatar_url && (
              (friend?.display_name?.charAt(0) || 
               friend?.username?.charAt(0) || 
               friend?.email?.charAt(0) || 
               'U').toUpperCase()
            )}
          </div>
          <h3>Supprimer cet ami ?</h3>
          <p>
            {friend?.display_name || 
             (friend?.username ? `@${friend.username}` : null) || 
             friend?.email || 
             'Utilisateur'}
          </p>
        </div>
        <div className="confirm-dialog-actions">
          <div
            className="confirm-btn-lottie confirm-btn"
            onClick={handleConfirm}
            style={{ 
              minWidth: '50px', 
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {acceptAnimation ? (
              <Lottie
                lottieRef={acceptLottieRef}
                animationData={acceptAnimation}
                loop={false}
                autoplay={false}
                style={{ width: 40, height: 40 }}
              />
            ) : (
              <span style={{ 
                fontSize: '32px', 
                color: '#51cf66',
                display: 'inline-block',
                lineHeight: '1',
                fontWeight: 'bold'
              }}>✓</span>
            )}
          </div>
          <div
            className="confirm-btn-lottie cancel-btn"
            onClick={handleCancel}
            style={{ 
              minWidth: '50px', 
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 10
            }}
          >
            {denyAnimation ? (
              <Lottie
                lottieRef={denyLottieRef}
                animationData={denyAnimation}
                loop={false}
                autoplay={false}
                style={{ width: 40, height: 40, position: 'relative', zIndex: 11 }}
              />
            ) : (
              <span style={{ 
                fontSize: '32px', 
                color: '#ff6b6b',
                display: 'inline-block',
                lineHeight: '1',
                fontWeight: 'bold',
                position: 'relative',
                zIndex: 11
              }}>✕</span>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

export default FriendsSidebar

